/**
 * Which validation failures are worth spelling out, and which are not.
 *
 * A field that is simply empty needs no sentence — the red border and red label
 * already say "this one, and it needs something". Printing "required" under it
 * adds a line of text that says nothing the colour has not, and in a joined row
 * like the booking dock it also breaks the layout as fields reflow around it.
 *
 * Everything else keeps its message. "Invalid email", "passwords do not match",
 * "must be a number", a date outside the allowed range — those tell the reader
 * something colour cannot, and going silent would leave them re-typing a value
 * that looks fine to them.
 */

/**
 * The keys the `required`-family validators in `validate.util` return.
 *
 * Kept as the raw keys rather than translated strings: validation runs before
 * i18n and the same key is shown in every language.
 */
const REQUIRED_ERROR_KEYS = new Set([
  // required / requiredPhone / requiredSelect, and `date` on an empty value
  "required",
  // requiredAutoComplete on an empty box
  "requiredLocation",
]);

/* Deliberately NOT in that set, though they come from the same validator:
 *
 *   selectOption  the box has text but no place was picked from the list
 *   notCity       the picked place resolves to no city
 *
 * Both fire on a field the reader has already filled in, so red alone reads as
 * "but I typed that" — the one state where colour cannot say what is wrong.
 * They keep their message. Move them up into the set to silence them too. */

/** True when this error only means "nothing here yet". */
export function isRequiredFieldError(error: unknown): boolean {
  return typeof error === "string" && REQUIRED_ERROR_KEYS.has(error);
}

/**
 * The helper text a field should show, given that it is in an error state.
 *
 * Pass the already-translated message; this only decides whether to show it.
 * Returns undefined for the silent cases so callers can hand it straight to
 * MUI, which then reserves no space for the line.
 */
export function fieldHelperText(
  showError: boolean,
  error: unknown,
  message: unknown,
): string | undefined {
  if (!showError || isRequiredFieldError(error)) return undefined;
  return typeof message === "string" && message ? message : undefined;
}
